import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { supabase } from './supabaseClient';

// Στατικό snapshot ΜΟΝΟ της προεπιλεγμένης γλώσσας (en), φτιαγμένο από το
// scripts/export-translations.js κατά το build. Είναι ήδη μέσα στο JS bundle
// -> μηδενική καθυστέρηση δικτύου, μηδέν flash raw keys στο πρώτο render.
// Τα υπόλοιπα languages (es, pt, gr) ΔΕΝ μπαίνουν στο bundle -> έρχονται
// αργότερα από το Supabase background fetch παρακάτω, χωρίς να βαραίνουν
// το αρχικό JS payload / FCP / LCP.
// Αν το αρχείο δεν υπάρχει ακόμα, τρέξε: node scripts/export-translations.js
import defaultLangTranslations from './locales/translations.en.json';

// Namespaces που χρησιμοποιούν flat key/value (δεν χρειάζονται unflatten)
// π.χ. packages: "solon.title" → δεν είναι nested i18n structure
const FLAT_NAMESPACES = ['packages']

function unflatten(flat) {
  const result = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let obj = result;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      const nextPart = parts[i + 1];
      const nextIsIndex = /^\d+$/.test(nextPart);
      if (obj[part] === undefined) {
        obj[part] = nextIsIndex ? [] : {};
      }
      // Αν το obj[part] δεν είναι object (π.χ. είναι string), αντικατέστησέ το
      if (typeof obj[part] !== 'object' || obj[part] === null) {
        obj[part] = nextIsIndex ? [] : {};
      }
      obj = obj[part];
    }
    const lastPart = parts[parts.length - 1];
    if (Array.isArray(obj)) {
      obj[Number(lastPart)] = value;
    } else {
      obj[lastPart] = value;
    }
  }

  function toArraysRecursive(obj) {
    if (Array.isArray(obj)) return obj.map(toArraysRecursive);
    if (typeof obj !== 'object' || obj === null) return obj;
    const keys = Object.keys(obj);
    const allNumeric = keys.length > 0 && keys.every(k => /^\d+$/.test(k));
    if (allNumeric) {
      return keys
        .sort((a, b) => Number(a) - Number(b))
        .map(k => toArraysRecursive(obj[k]));
    }
    const result = {};
    for (const k of keys) result[k] = toArraysRecursive(obj[k]);
    return result;
  }

  return toArraysRecursive(result);
}

async function loadTranslationsFromSupabase() {
  const PAGE_SIZE = 1000;
  let allRows = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from('translations')
      .select('lang, namespace, key, value')
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      console.error('Failed to load translations from Supabase:', error);
      break;
    }

    allRows = allRows.concat(data || []);

    if (!data || data.length < PAGE_SIZE) break; // τελευταία σελίδα
    from += PAGE_SIZE;
  }

  const resources = {};
  for (const { lang, namespace, key, value } of allRows) {
    if (!resources[lang]) resources[lang] = {};
    if (!resources[lang][namespace]) resources[lang][namespace] = {};
    resources[lang][namespace][key] = value;
  }

  for (const lang of Object.keys(resources)) {
    for (const ns of Object.keys(resources[lang])) {
      // Τα flat namespaces (π.χ. packages) δεν χρειάζονται unflatten —
      // χρησιμοποιούνται απευθείας με dot-notation keys από το PackagesArchive
      if (FLAT_NAMESPACES.includes(ns)) continue;
      resources[lang][ns] = unflatten(resources[lang][ns]);
    }
  }

  return resources;
}

// ── Αποθήκευση γλώσσας ──────────────────────────────────────────────────────
// Η επιλογή του χρήστη μένει στο localStorage, ώστε να μη γυρνάει σε EN
// μετά από refresh ή όταν ανοίγει link σε νέα καρτέλα.
const SUPPORTED_LANGS = ['en', 'es', 'pt', 'gr'];
const LANG_STORAGE_KEY = 'gsh_lang';
// Ο κωδικός 'gr' είναι εσωτερικός· το σωστό ISO για το <html lang> είναι 'el'
const HTML_LANG = { en: 'en', es: 'es', pt: 'pt', gr: 'el' };

function getSavedLanguage() {
  try {
    const saved = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (SUPPORTED_LANGS.includes(saved)) return saved;
  } catch { /* private mode / blocked storage */ }
  return 'en';
}

function persistLanguage(lng) {
  if (!SUPPORTED_LANGS.includes(lng)) return;
  try { window.localStorage.setItem(LANG_STORAGE_KEY, lng); } catch { /* ignore */ }
  if (typeof document !== 'undefined') document.documentElement.lang = HTML_LANG[lng] || lng;
}

i18n.on('languageChanged', persistLanguage);

let initPromise = null;

// Βήμα 1: αρχικοποίησε το i18next ΑΜΕΣΩΣ, συγχρονισμένα, με άδεια resources.
// Αυτό είναι κρίσιμο: χωρίς αυτό (ή με useSuspense: true), το react-i18next
// προσπαθεί να "suspend-άρει" τα components μέχρι να είναι έτοιμο, και χωρίς
// σωστό <Suspense> boundary αυτό προκαλεί τα "Should have a queue" /
// "prevDeps is undefined" σφάλματα που βλέπαμε.
function ensureInitialized() {
  if (!initPromise) {
    initPromise = i18n
      .use(initReactI18next)
      .init({
        resources: { en: defaultLangTranslations }, // μόνο en bundled -> instant, μικρό bundle
        defaultNS: 'nav',
        fallbackLng: 'en',
        lng: getSavedLanguage(), // μέχρι να φτάσουν οι μεταφράσεις, δείχνει EN (fallback)
        supportedLngs: SUPPORTED_LANGS,
        interpolation: { escapeValue: false },
        react: {
          useSuspense: false,
          bindI18nStore: 'added removed', // ξανακάνε render αν έρθουν πιο πρόσφατα δεδομένα από Supabase
        },
      });
  }
  return initPromise;
}

// Βήμα 2: φόρτωσε τα πραγματικά δεδομένα από το Supabase παράλληλα.
// Το addResourceBundle ενημερώνει αυτόματα κάθε component που χρησιμοποιεί
// useTranslation() — δεν χρειάζεται manual re-render ή reload.
export async function initI18n() {
  await ensureInitialized();

  const resources = loadTranslationsFromSupabase();

  resources
    .then((data) => {
      for (const lang of Object.keys(data)) {
        for (const ns of Object.keys(data[lang])) {
          i18n.addResourceBundle(lang, ns, data[lang][ns], true, true);
        }
      }
      // Ασφάλεια: ζόρισε ενημέρωση όλων των components ακόμα κι αν κάποιο
      // δεν πρόλαβε να "ακούσει" το store event έγκαιρα.
      i18n.emit('languageChanged', i18n.language);
    })
    .catch((err) => {
      console.error('Failed to load translations from Supabase:', err);
    });

  return i18n;
}

export default i18n;